import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { DISTRIBUTION_PROCESS_STATUS } from "@/constants/distributionStatus";
import { Check, CheckCheck, Minus } from "lucide-react";
import {
  ACTION_BUTTON,
  BADGE_BASE,
  BADGE_NEUTRAL,
  BADGE_STATUS,
  SURFACE_CARD,
  plural,
} from "../styles";
import {
  ContestGroupType,
  DistributionFileType,
  DistributionItemType,
  SelectionStateType,
} from "../types";
import { DistributionCard } from "./DistributionCard";

type ContestGroupProps = {
  contest: ContestGroupType;
  selectionState: SelectionStateType;
  isProcessing: boolean;
  filesMap: Record<number, DistributionFileType[]>;
  isLoadingFiles: boolean;
  isSelected: (uuid: string) => boolean;
  onToggleItem: (uuid: string) => void;
  onToggleContest: (contestId: string) => void;
  onAcceptContest: (contest: ContestGroupType) => void;
  onAcceptItem: (item: DistributionItemType) => void;
  onRejectItem: (item: DistributionItemType) => void;
};

/**
 * Checkbox de três estados. O Checkbox do shadcn desenha o mesmo ícone para
 * "checked" e "indeterminate", então aqui o indicador é próprio.
 */
const TriStateCheckbox = ({
  state,
  disabled,
  onToggle,
  label,
}: {
  state: SelectionStateType;
  disabled: boolean;
  onToggle: () => void;
  label: string;
}) => (
  <CheckboxPrimitive.Root
    checked={state === "all" ? true : state === "partial" ? "indeterminate" : false}
    disabled={disabled}
    onCheckedChange={onToggle}
    aria-label={label}
    className="peer h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground data-[state=indeterminate]:bg-primary data-[state=indeterminate]:text-primary-foreground"
  >
    <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
      {state === "partial" ? (
        <Minus className="h-4 w-4" />
      ) : (
        <Check className="h-4 w-4" />
      )}
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
);

export const ContestGroup = ({
  contest,
  selectionState,
  isProcessing,
  filesMap,
  isLoadingFiles,
  isSelected,
  onToggleItem,
  onToggleContest,
  onAcceptContest,
  onAcceptItem,
  onRejectItem,
}: ContestGroupProps) => {
  const pendingItems = contest.items.filter(
    (item) => item.statusId === DISTRIBUTION_PROCESS_STATUS.PENDING
  );
  const hasPending = pendingItems.length > 0;
  const pendingQuotas = pendingItems.reduce(
    (total, item) => total + (item.quotas ?? 0),
    0
  );

  const acceptAllButton = (className: string) => (
    <Button
      size="sm"
      variant="outline"
      disabled={isProcessing}
      onClick={() => onAcceptContest(contest)}
      className={`${ACTION_BUTTON.acceptOutline} ${className}`}
    >
      <CheckCheck className="mr-2 h-4 w-4" />
      Aceitar todos deste concurso
    </Button>
  );

  return (
    <AccordionItem
      value={contest.id}
      className={`overflow-hidden rounded-lg border ${SURFACE_CARD}`}
      // A cor da loteria vive só aqui e na bolinha: é identidade, não status.
      style={{ borderLeft: `4px solid ${contest.lotteryColor || "#71717a"}` }}
    >
      <div className="flex items-center gap-3 pl-3 pr-2">
        <TriStateCheckbox
          state={selectionState}
          disabled={!hasPending || isProcessing}
          onToggle={() => onToggleContest(contest.id)}
          label={`Selecionar pendentes do concurso ${contest.name ?? ""}`}
        />

        <div className="min-w-0 flex-1">
          <AccordionTrigger className="gap-2 py-3 hover:no-underline">
            <div className="flex min-w-0 flex-1 flex-col items-start gap-1.5 text-left">
              <span className="truncate font-semibold text-foreground">
                Concurso {contest.name || "Não informado"}
              </span>
              <span className="flex flex-wrap items-center gap-2">
                <span className={`${BADGE_BASE} ${BADGE_NEUTRAL}`}>
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: contest.lotteryColor || "#71717a" }}
                  />
                  {contest.lotteryName}
                </span>
                {hasPending && (
                  <span
                    className={`${BADGE_BASE} ${BADGE_STATUS[DISTRIBUTION_PROCESS_STATUS.PENDING]}`}
                  >
                    {pendingItems.length}{" "}
                    {plural(pendingItems.length, "pendente", "pendentes")} ·{" "}
                    {pendingQuotas} {plural(pendingQuotas, "cota", "cotas")}
                  </span>
                )}
                <span className="text-xs text-muted-foreground">
                  {contest.items.length}{" "}
                  {plural(contest.items.length, "distribuição", "distribuições")}
                </span>
              </span>
            </div>
          </AccordionTrigger>
        </div>

        {hasPending && acceptAllButton("hidden shrink-0 sm:inline-flex")}
      </div>

      <AccordionContent className="px-3 pb-3">
        {hasPending && acceptAllButton("mb-3 w-full sm:hidden")}

        <div className="flex flex-col gap-3">
          {contest.items.map((item) => (
            <DistributionCard
              key={item.uuid}
              item={item}
              files={filesMap[item.id] ?? []}
              isLoadingFiles={isLoadingFiles}
              isSelected={isSelected(item.uuid)}
              isProcessing={isProcessing}
              onToggle={onToggleItem}
              onAccept={onAcceptItem}
              onReject={onRejectItem}
            />
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
